import { RecordScopeProvider } from '@/api/RecordContext';
import { MeContext, useMeOrThrow } from '@/app/MeContext';
import { RecordPanelFrame, useFrameMaybe } from '@/app/frame';
import { TaskRoute } from '@/tasks/TaskPage';
import { ProjectPage } from '@/projects/ProjectPage';
import { ProspectPage } from '@/prospecting/ProspectPage';
import { ThreadPage } from '@/mail/ThreadPage';
import { CampaignRecord } from '@/outreach/Campaigns';
import { OfferPage, SupplierPage } from '@/validation/Validation';
import { BuyerPage, SignalPage } from '@/prospecting/Records';
import { RequestPage } from '@/requests/RequestPage';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { useCompanyOperations, useCompanyWorkspace, useExperience, usePersonalise, type WorkspaceItem } from '@/api/hooks';
import { failureOf } from '@/api/errors';
import { Refusal } from '@/ui/Refusal';
import { Badge, Button, Kicker, Skeleton, TextInput } from '@/ui/primitives';
import { Select } from '@/ui/fields';
import { Dialog } from '@/ui/Dialog';
import { useDepartmentLabel } from '@/app/departments';

const LABELS: Record<string,string>={task:'Tasks',project:'Projects',prospect:'Prospects',buyer:'Buyers',supplier:'Suppliers',signal:'Signals',campaign:'Campaigns',cadence:'Follow-up',request:'Requests',thread:'Conversations',attachment:'Files',meeting:'Meetings',offer:'Offers',research_step:'Research',manual_communication:'Manual communications'};

/** Query-string state survives record navigation, reload and browser Back. */
export function CompanyWorkspace({ companyId, preview=false }: {companyId:string;preview?:boolean}) {
 const me=useMeOrThrow();
 const frame=useFrameMaybe();
 const [params,setParams]=useSearchParams();
 const location=useLocation();
 const navigate=useNavigate();
 const departmentLabel=useDepartmentLabel();
 const query=preview?'':params.get('q')??'';
 const kind=preview?'':params.get('kind')??'';
 const department=params.get('department')??'';
 const status=preview?'':params.get('status')??'';
 const person=preview?'':params.get('person')??'';
 const agent=preview?'':params.get('agent')??'';
 const from=preview?'':params.get('from')??'';
 const to=preview?'':params.get('to')??'';
 const experience=useExperience();
 const personalise=usePersonalise();
 const savedView=experience.data?.resolved.views['company_workspace'];
 const pageSize=preview?30:(savedView?.pageSize??30);
 const order: 'latest'|'type'=savedView?.sortBy==='kind'?'type':'latest';
 const rawOffset=Number(params.get('offset')??0);
 const offset=preview?0:Number.isSafeInteger(rawOffset)&&rawOffset>=0?rawOffset:0;
 const q=useCompanyWorkspace(companyId,{query,kind,scopeDepartment:department,status,person,agent,from,to,offset,limit:pageSize,order,preview});
 const operations=useCompanyOperations(companyId);
 const openedButton=useRef<HTMLButtonElement|null>(null);
 const [selected,setSelected]=useState<WorkspaceItem|null>(null);
 const panel=params.get('item');
 const openedInWorkspace=useRef<string|null>(null);
 useEffect(()=>{
   if(!panel){openedInWorkspace.current=null;setSelected(null);return;}
   const item=q.data?.items.find(item=>`${item.department}:${item.kind}:${item.id}`===panel);
   if(item?.href&&frame?.workspaceOpener){
     if(openedInWorkspace.current===panel){setSelected(null);return;}
     if(frame.workspaceOpener(item.href,item.department)){openedInWorkspace.current=panel;setSelected(null);return;}
   }
   setSelected(item??null);
 },[panel,q.data,frame?.workspaceOpener]);
 const change=(key:string,value:string)=>{
   const next=new URLSearchParams(params);if(value) next.set(key,value);else next.delete(key);
   if(key!=='offset') next.delete('offset');
   setParams(next,{replace:true,preventScrollReset:true});
 };
 const saveView=(next:{pageSize?:number;order?:'latest'|'type'})=>{
   if(!experience.data)return;
   const personal=experience.data.line.at(-1)?.payload??{views:{},workflows:{},capabilities:{},preferences:{}};
   personalise.mutate({...personal,views:{...personal.views,company_workspace:{
     entity:'Company',columns:savedView?.columns??[],sortBy:(next.order??order)==='type'?'kind':'at',
     sortDescending:true,pageSize:next.pageSize??pageSize,
   }}});
 };
 const show=(item:WorkspaceItem,button:HTMLButtonElement)=>{
   openedButton.current=button;
   if(item.href&&frame?.workspaceOpener?.(item.href,item.department))return;
   setSelected(item); const next=new URLSearchParams(params);next.set('item',`${item.department}:${item.kind}:${item.id}`);setParams(next,{preventScrollReset:true});
 };
 const close=()=>{setSelected(null);if(!frame?.workspaceOpener&&panel && window.history.state?.idx>0) void navigate(-1);else {const next=new URLSearchParams(params);next.delete('item');setParams(next,{replace:true,preventScrollReset:true});}};
 if(q.isPending) return <Skeleton rows={4}/>;
 if(q.isError) return <Refusal failure={failureOf(q.error)}/>;
 const items=q.data.items;
 const shown=preview?[...items].sort((a,b)=>sectionOrder(a.section)-sectionOrder(b.section)||(a.attentionRank??100)-(b.attentionRank??100)||b.at.localeCompare(a.at)):items;
 const groups=Object.entries(q.data.counts);
 return <section className="flex flex-col gap-3" aria-label={preview?'Latest company work':'All company work'}>
   <Kicker>{preview?'Latest work and attention':'Company records'} · {q.data.total}</Kicker>
   {!preview&&<div className="flex flex-wrap gap-3">
     <TextInput aria-label="Search company records" placeholder="Search all related records" value={query} onChange={e=>change('q',e.target.value)}/>
     <Select aria-label="Record type" value={kind} onChange={e=>change('kind',e.target.value)} options={[{value:'',label:'All types'},...groups.map(([value,count])=>({value,label:`${LABELS[value]??value} · ${count}`}))]}/>
     <Select aria-label="Department" value={department} onChange={e=>change('department',e.target.value)} options={[{value:'',label:'All accessible departments'},...q.data.departments.map(value=>({value,label:departmentLabel(value)}))]}/>
     <TextInput aria-label="Status" placeholder="Any status" value={status} onChange={e=>change('status',e.target.value)}/>
     <TextInput aria-label="Person" placeholder="Any person" value={person} onChange={e=>change('person',e.target.value)}/>
     <TextInput aria-label="Agent" placeholder="Any agent" value={agent} onChange={e=>change('agent',e.target.value)}/>
     <TextInput aria-label="From date" type="date" value={from} onChange={e=>change('from',e.target.value)}/>
     <TextInput aria-label="To date" type="date" value={to} onChange={e=>change('to',e.target.value)}/>
     <Select aria-label="Record order" value={order} onChange={e=>saveView({order:e.target.value as 'latest'|'type'})} options={[{value:'latest',label:'Latest first'},{value:'type',label:'Group by type'}]}/>
     <Select aria-label="Rows per page" value={String(pageSize)} onChange={e=>saveView({pageSize:Number(e.target.value)})} options={[10,30,50,100].map(value=>({value:String(value),label:`${value} rows`}))}/>
   </div>}
   <p className="m-0 text-label text-meta">Read {new Date(q.data.readAt).toLocaleString('en-GB')}. Source records retain their own connection and sync status.</p>
   {operations.data&&<div className="flex flex-wrap gap-2 text-dense" aria-label="Company relationships and sources">
     {operations.data.roles.map(role=><Badge key={role.id}>{role.role.toUpperCase()} · {role.state}</Badge>)}
     {operations.data.groups.map(group=><Badge key={group.id}>{group.relation.replaceAll('_',' ')} · {group.related_company_name}</Badge>)}
     {operations.data.enrichment.filter(item=>item.state==='needs_review').length>0&&<Badge tone="warning">{operations.data.enrichment.filter(item=>item.state==='needs_review').length} ENRICHMENT REVIEW</Badge>}
     {operations.data.freshness.map(source=><Badge key={source.id} tone={source.state==='failed'?'danger':'neutral'}>{source.source_kind} · {source.state.replaceAll('_',' ')}</Badge>)}
   </div>}
   {shown.length===0?<p className="m-0 text-dense text-secondary">{query||kind?'No records match these filters.':'No linked work is visible in this scope yet.'}</p>:<ul className="m-0 flex list-none flex-col gap-2 p-0">
     {shown.map((item,index)=><Fragment key={`${item.department}:${item.kind}:${item.id}`}>
       {preview&&(index===0||shown[index-1]?.section!==item.section)&&<li className="mt-2 text-label font-medium uppercase tracking-wide text-secondary">{sectionLabel(item.section)}</li>}
       <li className="flex flex-wrap items-center gap-2 border border-line p-3">
       <div className="min-w-0 flex-1">
         <Button ref={button=>{if(panel===`${item.department}:${item.kind}:${item.id}`) openedButton.current=button;}} variant="quiet" onClick={event=>show(item,event.currentTarget)}>{item.title}</Button>
         {item.excerpt&&<p className="m-0 line-clamp-2 text-dense text-secondary">{item.excerpt}</p>}
         {item.attentionReason&&<p className="m-0 text-dense text-warning">{item.attentionReason}{item.nextAction?` · ${item.nextAction}`:''}</p>}
       </div>
       <Badge>{LABELS[item.kind]??item.kind}</Badge><span className="text-label text-meta">{(item.sourceDepartment===null?'Personal inbox':departmentLabel(item.department))} · {item.state??'On record'} · {new Date(item.at).toLocaleDateString('en-GB')}</span>
       </li>
     </Fragment>)}
   </ul>}
   {preview&&<div className="flex flex-wrap gap-2">{groups.map(([type,count])=><Button key={type} size="row" onClick={()=>{const next=new URLSearchParams(params);next.set('tab','records');next.set('kind',type);next.delete('offset');setParams(next,{replace:true,preventScrollReset:true});}}>{LABELS[type]??type} · {count}</Button>)}</div>}
   {preview?<Button className="self-start" onClick={()=>change('tab','records')}>All related work</Button>:<div className="flex items-center gap-3">
     <Button disabled={offset===0} onClick={()=>change('offset',String(Math.max(0,offset-q.data.limit)))}>Previous</Button>
     <span className="text-label">{q.data.total?offset+1:0}–{Math.min(offset+q.data.limit,q.data.total)} of {q.data.total}</span>
     <Button disabled={offset+q.data.limit>=q.data.total} onClick={()=>change('offset',String(offset+q.data.limit))}>Next</Button>
   </div>}
   {selected&&panel&&<Dialog size="record" onCloseAutoFocus={event=>{if(openedButton.current){event.preventDefault();openedButton.current.focus();}}} open onOpenChange={o=>!o&&close()} kicker={`${LABELS[selected.kind]??selected.kind} · ${(selected.sourceDepartment===null?'Personal inbox':departmentLabel(selected.department))}`} title={selected.title} secondary={{label:'Back to company',onClick:close}}>
     <RecordScopeProvider scope={recordScope(companyId,selected)}>
       <MeContext.Provider value={{...me,department:selected.department}}>
         <RecordPanelFrame>
           <CompanyRecord item={selected} onClose={close}/>
         </RecordPanelFrame>
       </MeContext.Provider>
     </RecordScopeProvider>
     {selected.href&&<Link to={selected.href} state={{companyReturn:{to:location.pathname+location.search,label:'Company',scopeDepartment:selected.department}}} className="text-accent">Open as a page</Link>}
   </Dialog>}
 </section>;
}

function sectionOrder(section:WorkspaceItem['section']):number{return section==='attention'?0:section==='planned'?1:2;}
function sectionLabel(section:WorkspaceItem['section']):string{return section==='attention'?'Needs attention':section==='planned'?'Planned work':'Latest activity';}

function CompanyRecord({item,onClose}:{item:WorkspaceItem;onClose:()=>void}) {
 switch(item.kind) {
 case 'thread':return <ThreadPage recordId={item.id}/>;
 case 'offer':return <OfferPage recordId={item.id}/>;
 case 'campaign':return <CampaignInCompany item={item} onClose={onClose}/>;
 case 'cadence':case 'research_step':return <ProspectPage recordId={item.href?.split('/').at(-1)??item.id}/>;
 case 'meeting':return <ProjectPage recordId={item.href?.split('/').at(-1)??item.id}/>;
 case 'task':return <TaskRoute recordId={item.id}/>;
 case 'project':return <ProjectPage recordId={item.id}/>;
 case 'prospect':return <ProspectPage recordId={item.id}/>;
 case 'supplier':return <SupplierPage recordId={item.id}/>;
 case 'buyer':return <BuyerPage recordId={item.id}/>;
 case 'signal':return <SignalPage recordId={item.id}/>;
 case 'request':return <RequestPage recordId={item.id}/>;
 default:return <dl className="text-dense"><dt>Status</dt><dd>{item.state??'On record'}</dd><dt>Recorded</dt><dd>{new Date(item.at).toLocaleString('en-GB')}</dd></dl>;
 }
}

function CampaignInCompany({item,onClose}:{item:WorkspaceItem;onClose:()=>void}) {
 const me=useMeOrThrow();return <CampaignRecord id={item.id} mayDecide={me.isSuperadmin||me.leads.includes(item.department)} onClose={onClose}/>;
}
function recordScope(companyId:string,item:WorkspaceItem) {
 const parentKind=['cadence','research_step'].includes(item.kind)?'prospect':item.kind==='meeting'?'project':item.kind;
 return {companyId,sourceKind:parentKind,sourceId:parentKind!==item.kind?(item.href?.split('/').at(-1)??item.id):item.id,scopeDepartment:item.department};
}
